import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../../store/auth'
import { API_BASE } from '../../../lib/api'
import { messagingAttachmentQueryRoot } from '../../../api/attachments'
import type { MessagingUnreadSurface } from './useMessagingUnread'

type RealtimeState = 'disabled' | 'connecting' | 'live' | 'fallback'

function isMessagingQuery(queryKey: readonly unknown[]) {
  const root = queryKey[0]
  return typeof root === 'string' && root.includes('/messaging')
}

export function useMessagingRealtime(
  surface: MessagingUnreadSurface,
  enabled: boolean,
) {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((state) => state.accessToken)
  const canConnect =
    enabled && Boolean(accessToken) && typeof WebSocket !== 'undefined'
  const [state, setState] = useState<RealtimeState>('connecting')

  useEffect(() => {
    if (!canConnect || !accessToken) return

    let socket: WebSocket | null = null
    let reconnectTimer: number | undefined
    let closed = false
    let attempt = 0
    const broadcast =
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel(`pexify-messaging-${surface}`)

    const reconcile = () => {
      void queryClient.invalidateQueries({
        predicate: (query) => isMessagingQuery(query.queryKey),
      })
      void queryClient.invalidateQueries({
        queryKey: messagingAttachmentQueryRoot(),
      })
    }
    broadcast?.addEventListener('message', reconcile)

    const connect = () => {
      if (closed || document.visibilityState === 'hidden') return
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer)
        reconnectTimer = undefined
      }
      if (socket && socket.readyState < WebSocket.CLOSING) return
      setState('connecting')
      const apiOrigin = new URL(API_BASE, window.location.origin)
      const scheme = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:'
      socket = new WebSocket(
        `${scheme}//${apiOrigin.host}/api/v1/messaging/realtime`,
      )
      socket.addEventListener('open', () => {
        socket?.send(JSON.stringify({ type: 'auth', token: accessToken }))
      })
      socket.addEventListener('message', (message) => {
        let payload: { type?: string }
        try {
          payload = JSON.parse(String(message.data)) as { type?: string }
        } catch {
          return
        }
        if (payload.type === 'ready') {
          attempt = 0
          setState('live')
          reconcile()
        } else if (payload.type === 'invalidate') {
          reconcile()
          broadcast?.postMessage({ type: 'invalidate' })
        } else if (payload.type === 'ping') {
          socket?.send(JSON.stringify({ type: 'pong' }))
        }
      })
      socket.addEventListener('close', () => {
        if (closed) return
        socket = null
        setState('fallback')
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5))
        attempt += 1
        reconnectTimer = window.setTimeout(connect, delay)
      })
      socket.addEventListener('error', () => socket?.close())
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        reconcile()
        if (!socket || socket.readyState >= WebSocket.CLOSING) connect()
      } else if (socket?.readyState === WebSocket.OPEN) {
        socket.close(1000, 'tab_hidden')
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    connect()

    return () => {
      closed = true
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer)
      document.removeEventListener('visibilitychange', onVisibility)
      broadcast?.removeEventListener('message', reconcile)
      broadcast?.close()
      socket?.close(1000, 'component_unmounted')
    }
  }, [accessToken, canConnect, queryClient, surface])

  if (!enabled) return 'disabled'
  if (!canConnect) return 'fallback'
  return state
}
